import { parsePointKey } from '../core/GridGeometry.js';
import { Grid } from '../core/Grid.js';
import { RenderConfig, COLORS, colorWithAlpha, canvasFont } from '../rendering/Renderer.js';

// ── Types ──────────────────────────────────────────────────────────

export type TextAnchor =
    | 'above' | 'below' | 'left' | 'right' | 'center'
    | 'near-above' | 'near-below' | 'near-left' | 'near-right'
    | 'far-above' | 'far-below' | 'far-left' | 'far-right';

export interface TextLabelAnnotation {
    type: 'text-label';
    text: string;
    /** Cell position string, e.g. "A0" — anchors at cell center. */
    cell?: string;
    /** Grid point string, e.g. "30" — anchors at grid intersection. */
    point?: string;
    /** Edge string (point-based), e.g. "0203" — anchors at edge midpoint. */
    edge?: string;
    anchor: TextAnchor;
    color?: string;
    fontSize?: number;
}

export interface HighlightAnnotation {
    type: 'highlight';
    cells: string[];
    color: string;
}

export interface ExclusionAnnotation {
    type: 'exclusion';
    cells: string[];
}

export interface PulseAnnotation {
    type: 'pulse';
    /** Cell position string, e.g. "A0" — anchors at cell center. */
    cell?: string;
    /** Grid point string, e.g. "30" — anchors at grid intersection. */
    point?: string;
    /** Edge string (point-based), e.g. "0203" — anchors at edge midpoint. */
    edge?: string;
    color?: string;
}

export type TutorialAnnotation =
    | TextLabelAnnotation
    | HighlightAnnotation
    | ExclusionAnnotation
    | PulseAnnotation;

export interface SceneAnimation {
    /** Milliseconds between frames. */
    interval: number;
    /** Whether to loop back to first frame after last. */
    loop: boolean;
    /** Each frame is a game state configuration replacing the scene's gameState. */
    frames: unknown[];
}

export interface TutorialScene {
    /** Game-specific state to apply (e.g., mirror placements, wall positions). */
    gameState: unknown;
    /** Game-specific render options (e.g., which overlays to show). */
    renderOptions: unknown;
    annotations: TutorialAnnotation[];
    /** Optional animation that cycles through game state configurations. */
    animate?: SceneAnimation;
}

export interface TutorialGameAdapter {
    /** Reset the game and apply a scene's game state. */
    applyState(state: unknown): void;
    /** Render the game with scene-specific display options. */
    render(options: unknown): void;
    /** Get the render config for coordinate calculations. */
    getConfig(): RenderConfig;
}

export type MetaTutorialId = 'basics' | 'vortex-hub';

export interface TutorialDefinition {
    gameType: string;
    gridCode: string;
    scenes: TutorialScene[];
    /** Scene index to use for the hub preview (defaults to 0). */
    previewScene?: number;
}

export interface TutorialCallbacks {
    onExit: () => void;
    onSceneChange: (sceneIndex: number, totalScenes: number) => void;
}

// ── Engine ─────────────────────────────────────────────────────────

export class TutorialEngine {
    private definition: TutorialDefinition;
    private callbacks: TutorialCallbacks;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;

    private adapter: TutorialGameAdapter;
    private grid: Grid;
    private config: RenderConfig;

    private currentSceneIndex: number = 0;
    private active: boolean = false;

    // Animation state
    private animationFrameId: number | null = null;
    private animationStartTime: number = 0;
    private currentAnimateFrame: number = 0;

    private boundClick: (e: MouseEvent) => void;
    private boundContextMenu: (e: MouseEvent) => void;

    constructor(
        definition: TutorialDefinition,
        canvas: HTMLCanvasElement,
        grid: Grid,
        adapter: TutorialGameAdapter,
        callbacks: TutorialCallbacks
    ) {
        this.definition = definition;
        this.canvas = canvas;
        this.callbacks = callbacks;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get 2D context');
        this.ctx = ctx;

        this.grid = grid;
        this.adapter = adapter;
        this.config = adapter.getConfig();

        this.boundClick = this.onClick.bind(this);
        this.boundContextMenu = this.onContextMenu.bind(this);
    }

    start(): void {
        this.active = true;
        this.currentSceneIndex = 0;
        this.canvas.addEventListener('click', this.boundClick);
        this.canvas.addEventListener('contextmenu', this.boundContextMenu);
        this.applyScene();
        this.render();
    }

    stop(): void {
        this.active = false;
        this.stopAnimationLoop();
        this.canvas.removeEventListener('click', this.boundClick);
        this.canvas.removeEventListener('contextmenu', this.boundContextMenu);
    }

    isActive(): boolean {
        return this.active;
    }

    /** Public render for resize handling. */
    render(): void {
        if (!this.active) return;
        this.renderFrame(performance.now());
    }

    // ── Scene management ───────────────────────────────────────────

    private applyScene(): void {
        this.stopAnimationLoop();
        const scene = this.definition.scenes[this.currentSceneIndex];
        this.adapter.applyState(scene.gameState);
        this.currentAnimateFrame = 0;
        this.callbacks.onSceneChange(
            this.currentSceneIndex,
            this.definition.scenes.length
        );
        if (this.needsAnimation(scene)) {
            this.startAnimationLoop();
        }
    }

    private advanceScene(): void {
        this.currentSceneIndex =
            (this.currentSceneIndex + 1) % this.definition.scenes.length;
        this.applyScene();
        this.render();
    }

    // ── Animation loop ────────────────────────────────────────────

    private needsAnimation(scene: TutorialScene): boolean {
        if (scene.animate) return true;
        return scene.annotations.some(a => a.type === 'pulse');
    }

    private startAnimationLoop(): void {
        this.animationStartTime = performance.now();
        const loop = (timestamp: number) => {
            if (!this.active) return;
            this.updateAnimation(timestamp);
            this.renderFrame(timestamp);
            this.animationFrameId = requestAnimationFrame(loop);
        };
        this.animationFrameId = requestAnimationFrame(loop);
    }

    private stopAnimationLoop(): void {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    private updateAnimation(timestamp: number): void {
        const scene = this.definition.scenes[this.currentSceneIndex];
        if (!scene.animate) return;

        const elapsed = Math.max(0, timestamp - this.animationStartTime);
        const frameCount = scene.animate.frames.length;
        const rawFrame = Math.floor(elapsed / scene.animate.interval);
        const newFrame = scene.animate.loop
            ? rawFrame % frameCount
            : Math.min(rawFrame, frameCount - 1);

        if (newFrame !== this.currentAnimateFrame) {
            this.currentAnimateFrame = newFrame;
            this.adapter.applyState(scene.animate.frames[newFrame]);
        }
    }

    private renderFrame(timestamp: number): void {
        const scene = this.definition.scenes[this.currentSceneIndex];

        this.adapter.render(scene.renderOptions);

        this.drawAnnotations(scene.annotations, timestamp);
        // Scene progress is rendered above the grid (DOM, via onSceneChange) — not
        // on the canvas — so it sits where the old "N / total" text was.
    }

    // ── Input ──────────────────────────────────────────────────────

    private onClick(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.advanceScene();
    }

    private onContextMenu(e: MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
    }

    // ── Annotation rendering ───────────────────────────────────────

    private drawAnnotations(annotations: TutorialAnnotation[], timestamp: number): void {
        for (const a of annotations) {
            switch (a.type) {
                case 'text-label':
                    this.drawTextLabel(a);
                    break;
                case 'highlight':
                    this.drawHighlight(a);
                    break;
                case 'exclusion':
                    this.drawExclusion(a);
                    break;
                case 'pulse':
                    this.drawPulse(a, timestamp);
                    break;
            }
        }
    }

    private drawTextLabel(a: TextLabelAnnotation): void {
        const fontSize = a.fontSize ?? 14;
        const color = a.color ?? COLORS.white;
        const center = a.edge ? this.getEdgeMidpointPixel(a.edge)
            : a.point ? this.getPointPixel(a.point)
            : this.getCellCenter(a.cell!);

        this.ctx.font = canvasFont(fontSize);
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';

        const lines = a.text.split('\n');
        const lineHeight = fontSize * 1.3;
        const lineWidths = lines.map(line => this.ctx.measureText(line).width);
        const textW = Math.max(...lineWidths);
        const textH = fontSize + (lines.length - 1) * lineHeight;
        const padX = 8;
        const padY = 6;

        // Compute label center based on anchor
        let x = center.x;
        let y = center.y;
        let offsetFactor = 0.7;

        switch (a.anchor) {
            case 'far-above':
            case 'far-below':
            case 'far-left':
            case 'far-right':
                offsetFactor = 1;
                break;
            case 'near-above':
            case 'near-below':
            case 'near-left':
            case 'near-right':
                offsetFactor = 0.35;
                break;
        }
        const offset = this.config.cellSize * offsetFactor;

        switch (a.anchor) {
            case 'above':
            case 'near-above':
            case 'far-above':
                y = center.y - offset;
                break;
            case 'below':
            case 'near-below':
            case 'far-below':
                y = center.y + offset;
                break;
            case 'left':
            case 'near-left':
            case 'far-left':
                x = center.x - offset - textW / 2;
                break;
            case 'right':
            case 'near-right':
            case 'far-right':
                x = center.x + offset + textW / 2;
                break;
            case 'center':
                break;
        }

        // Draw background pill
        const bgX = x - textW / 2 - padX;
        const bgY = y - textH / 2 - padY;
        const bgW = textW + padX * 2;
        const bgH = textH + padY * 2;
        const radius = 6;

        this.ctx.fillStyle = colorWithAlpha('background', 0.85);
        this.ctx.beginPath();
        this.ctx.moveTo(bgX + radius, bgY);
        this.ctx.lineTo(bgX + bgW - radius, bgY);
        this.ctx.quadraticCurveTo(bgX + bgW, bgY, bgX + bgW, bgY + radius);
        this.ctx.lineTo(bgX + bgW, bgY + bgH - radius);
        this.ctx.quadraticCurveTo(bgX + bgW, bgY + bgH, bgX + bgW - radius, bgY + bgH);
        this.ctx.lineTo(bgX + radius, bgY + bgH);
        this.ctx.quadraticCurveTo(bgX, bgY + bgH, bgX, bgY + bgH - radius);
        this.ctx.lineTo(bgX, bgY + radius);
        this.ctx.quadraticCurveTo(bgX, bgY, bgX + radius, bgY);
        this.ctx.closePath();
        this.ctx.fill();

        // Draw text lines
        this.ctx.fillStyle = color;
        const topLineY = y - (lines.length - 1) * lineHeight / 2;
        for (let i = 0; i < lines.length; i++) {
            this.ctx.fillText(lines[i], x, topLineY + i * lineHeight + 1);
        }
    }

    private drawHighlight(a: HighlightAnnotation): void {
        const { cellSize } = this.config;
        this.ctx.fillStyle = a.color;
        for (const pos of a.cells) {
            const tl = this.getCellTopLeft(pos);
            this.ctx.fillRect(tl.x, tl.y, cellSize, cellSize);
        }
    }

    private drawExclusion(a: ExclusionAnnotation): void {
        const { cellSize } = this.config;
        const inset = cellSize * 0.2;

        for (const pos of a.cells) {
            const tl = this.getCellTopLeft(pos);

            // Red fill
            this.ctx.fillStyle = colorWithAlpha('red', 0.2);
            this.ctx.fillRect(tl.x, tl.y, cellSize, cellSize);

            // X through cell
            this.ctx.strokeStyle = colorWithAlpha('red', 0.6);
            this.ctx.lineWidth = 2;
            this.ctx.lineCap = 'round';
            this.ctx.beginPath();
            this.ctx.moveTo(tl.x + inset, tl.y + inset);
            this.ctx.lineTo(tl.x + cellSize - inset, tl.y + cellSize - inset);
            this.ctx.moveTo(tl.x + cellSize - inset, tl.y + inset);
            this.ctx.lineTo(tl.x + inset, tl.y + cellSize - inset);
            this.ctx.stroke();
        }
    }

    private drawPulse(a: PulseAnnotation, timestamp: number): void {
        const color = a.color ?? COLORS.teal;
        const center = a.edge ? this.getEdgeMidpointPixel(a.edge)
            : a.point ? this.getPointPixel(a.point)
            : this.getCellCenter(a.cell!);
        const { cellSize } = this.config;

        const duration = 1500; // ms per cycle
        const t = ((timestamp - this.animationStartTime) % duration) / duration;

        const minRadius = cellSize * 0.35;
        const maxRadius = cellSize * 0.55;
        const radius = minRadius + (maxRadius - minRadius) * t;

        this.ctx.save();
        this.ctx.globalAlpha = 1 - t * 0.7;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = 3 - t * 1.5;
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
        this.ctx.stroke();
        this.ctx.restore();
    }

    // ── Coordinate helpers ─────────────────────────────────────────

    /** Parse position string "A0" → pixel at cell center. */
    private getCellCenter(s: string): { x: number; y: number } {
        const pos = this.grid.serializer.deserializePosition(s);
        return {
            x: this.config.padding + pos.col * this.config.cellSize + this.config.cellSize / 2,
            y: this.config.padding + pos.row * this.config.cellSize + this.config.cellSize / 2,
        };
    }

    /** Parse position string "A0" → pixel at cell top-left. */
    private getCellTopLeft(s: string): { x: number; y: number } {
        const pos = this.grid.serializer.deserializePosition(s);
        return {
            x: this.config.padding + pos.col * this.config.cellSize,
            y: this.config.padding + pos.row * this.config.cellSize,
        };
    }

    /** Parse point string "30" → pixel at grid intersection. */
    private getPointPixel(s: string): { x: number; y: number } {
        const p = parsePointKey(s);
        return {
            x: this.config.padding + p.col * this.config.cellSize,
            y: this.config.padding + p.row * this.config.cellSize,
        };
    }

    /** Parse edge string "0203" → pixel at edge midpoint. */
    private getEdgeMidpointPixel(s: string): { x: number; y: number } {
        const pa = parsePointKey(s.slice(0, 2));
        const pb = parsePointKey(s.slice(2, 4));
        return {
            x: this.config.padding + (pa.col + pb.col) / 2 * this.config.cellSize,
            y: this.config.padding + (pa.row + pb.row) / 2 * this.config.cellSize,
        };
    }
}
